import { memo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { motion, Variants } from 'framer-motion'
import clsx from 'clsx'

import { EReaction } from '@/src/constants/reactions'
import { useReactions } from '@/src/hooks/room/reactions/useReactions'
import { useClickOutside } from '@/src/hooks/useClickOutside.hook'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

import styles from './Reactions.module.css'

const popoverVariants: Variants = {
  hidden: {
    opacity: 0,
    x: '-50%',
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    x: '-50%',
    scale: 1,
    transition: {
      type: 'spring',
      damping: 20,
      stiffness: 300,
    },
  },
  exit: {
    opacity: 0,
    x: '-50%',
    y: 10,
    transition: { duration: 0.15 },
  },
}

const buttonVariants: Variants = {
  tap: { scale: 0.9 },
}

interface IReactionsProps {
  className?: string

  isPiP?: boolean

  closePopover?: () => void
}

export const Reactions = memo(function memoReactions({
  className,
  isPiP,
  closePopover,
}: IReactionsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { text_primary_color } = useAppSelector(selectBranding)

  const { t } = useTranslation()
  const { handleRaiseHandToggle, handleQuickReaction, isHandRaised } = useReactions()

  useClickOutside(containerRef, closePopover, true, 200)

  return (
    <motion.div
      ref={containerRef}
      className={clsx(styles?.popover, className, {
        [styles?.pipPopover]: isPiP,
      })}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={popoverVariants}
    >
      <ul className={styles?.reactionsContainer}>
        {Object.keys(EReaction).map(
          (reaction: keyof typeof EReaction) =>
            reaction !== 'default' &&
            reaction !== 'raiseHand' && (
              <motion.button
                key={reaction}
                onClick={() => handleQuickReaction(reaction)}
                transition={{ duration: 0.01 }}
                variants={buttonVariants}
                whileTap="tap"
              >
                {EReaction[reaction]}
              </motion.button>
            ),
        )}
      </ul>

      <motion.button
        style={{ color: text_primary_color ?? '#fff' }}
        className={clsx(styles?.raiseHandBtn, {
          [styles?.activeReaction]: isHandRaised,
          [styles?.pipRaiseHandBtn]: isPiP,
        })}
        onClick={handleRaiseHandToggle}
        transition={{ duration: 0.01 }}
        variants={buttonVariants}
        whileTap="tap"
      >
        <span className={styles?.raiseHandBtnIcon}>🖐️</span>
        {isHandRaised ? t('reactions.lowerHand') : t('reactions.raiseHand')}
      </motion.button>
    </motion.div>
  )
})
